// src/utils/defaultPage.js
// Utilities for storing and resolving per-user default landing pages.

import { PAGE_DEFINITIONS } from "../config/permissions";

export const SAFE_FALLBACK_ROUTE = "/no-access";

// Sidebar labels differ from PAGE_DEFINITIONS for a few entries; override where needed.
const PAGE_LABEL_OVERRIDES = {
  pos: "PO Details",
  mrp: "MRP AIO",
  "mrp-inventory": "MRP Inventory",
  "mrp-container-details": "MRP Container Details",
  "mrp-products": "MRP Products",
  "raw-vessels": "raw_vessels",
  "raw-vessel-containers": "raw_vessel_containers",
  "raw-vessel-consignees": "raw_vessel_consignees",
  "my-containers": "My Containers",
  "admin-home": "Admin Home",
  "admin-roles": "Roles",
  "admin-users": "Users",
  "admin-drayage": "Drayage",
  "admin-warehouse": "Warehouse",
  "admin-customers": "Customers",
  "admin-logs": "Log",
  "admin-api-logs": "API Logs",
};

const DISALLOWED_LEVELS = new Set(["none", "no access"]);

function mapPageDefinition(page) {
  return {
    id: page.id,
    label: PAGE_LABEL_OVERRIDES[page.id] || page.label || page.route,
    route: page.route,
  };
}

export function getAllowedPagesForRole(roleName, permissionGetLevelFn) {
  if (typeof permissionGetLevelFn !== "function") return [];
  const role = roleName || "";
  return PAGE_DEFINITIONS.filter((page) => page.route)
    .map((page) => {
      const level = permissionGetLevelFn(role, page.id);
      if (!level || DISALLOWED_LEVELS.has(String(level).trim().toLowerCase())) {
        return null;
      }
      return mapPageDefinition(page);
    })
    .filter(Boolean);
}

export function buildDefaultPageOptions(getLevel, roleName) {
  return getAllowedPagesForRole(roleName, getLevel);
}

export function getRouteForPageId(pageId) {
  if (!pageId) return null;
  const match = PAGE_DEFINITIONS.find((page) => page.id === pageId);
  return match?.route || null;
}

export function resolveDefaultRoute({
  user,
  role,
  getLevel,
  storedDefaultRoute,
  roleDefaultRoute,
  userDefaultPageId,
  roleDefaultPageId,
  allowedPagesOverride,
} = {}) {
  const normalizedRole = role || user?.role;
  const allowedPages = Array.isArray(allowedPagesOverride)
    ? allowedPagesOverride
    : getAllowedPagesForRole(normalizedRole, getLevel);
  const allowedRoutes = new Set(allowedPages.map((page) => page.route));
  const userDefaultRoute =
    storedDefaultRoute ||
    getRouteForPageId(userDefaultPageId || user?.default_page_id || user?.default_page);
  const roleRoute =
    roleDefaultRoute ||
    getRouteForPageId(roleDefaultPageId || user?.role_default_page_id);
  const storedRoute =
    userDefaultRoute ||
    user?.landing_route ||
    null;

  if (storedRoute && allowedRoutes.has(storedRoute)) {
    return storedRoute;
  }

  if (roleRoute && allowedRoutes.has(roleRoute)) {
    return roleRoute;
  }

  if (allowedPages.length > 0) {
    return allowedPages[0].route;
  }

  return SAFE_FALLBACK_ROUTE;
}
